import { inject } from '@angular/core';
import { selectProfileBillingAddressById } from '@app/app.reducer';
import { FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { PersonType } from '@core/data-access/models/person.model';
import { BuyerFormsSyncActions } from '@core/data-access/store/form/form.actions';
import { selectIsReadOnlyBuyerFormFields } from '@core/data-access/store/helper.selectors';
import { AnonymousVoucherActions } from '@core/data-access/store/shared/shared.actions';
import { SavedTicketHolderActions } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.actions';
import {
  selectSavedBuyer,
  selectSavedBuyerPerson
} from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.selectors';
import {
  BuyerBillingAddressFormActions,
  BuyerInformationActions,
  BuyerInformationFormActions
} from '@core/features/buyer-information/store/buyer-information.actions';
import {
  selectBuyerInformationFormFields,
  selectBuyerPerson
} from '@core/features/buyer-information/store/buyer-information.selectors';
import {
  disablePrefilledSavedBuyerFormFields,
  enableDisabledFormFields
} from '@core/utils/form-fields/form-field-helpers';
import { combineTranslatedFormOptionKeyAndValue } from '@core/utils/form-helpers';
import { areBuyerFormsFieldsEqual } from '@features/buyer-information/utils/are-buyer-fields-equal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getUUID } from '@shared/app-utils';
import { ActionTypes as CustomizationActionTypes } from '@store/customization/customization.actions';
import { GetProfileBillingAddress, SetProfile, ActionTypes as UserActionTypes } from '@store/user/user.actions';
import { find, isEmpty, isEqual, isNil, isNull, mapValues } from 'lodash';
import { filter, map } from 'rxjs';
import { SetSelectedSavedBillingAddress } from '../models/buyer-information-store.model';

export const setBuyerInformationPersonOnExhibitionSelect$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(CustomizationActionTypes.SET_ORDER_UUID, AnonymousVoucherActions.anonymousVoucherIsReleased),
      concatLatestFrom(() => [store.select(selectSavedBuyerPerson), store.select(selectBuyerPerson)]),
      map(([_, savedBuyerPerson, buyerPerson]) => {
        if (!isNil(savedBuyerPerson)) {
          return savedBuyerPerson;
        }

        if (!isNil(buyerPerson)) {
          return buyerPerson;
        }

        return { id: null, uuid: getUUID(), type: PersonType.Buyer };
      }),
      map((person) => BuyerInformationActions.setBuyerPerson(person))
    ),
  { functional: true }
);

export const setBuyerInformationPrefillAndFormFields$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(BuyerFormsSyncActions.setBuyerInformationFormPrefill),
      concatLatestFrom(() => store.select(selectIsReadOnlyBuyerFormFields)),
      map(([{ prefillBuyerInformationForm, person, formFields: savedBuyerFormFields }, isReadOnlyBuyerFormFields]) =>
        BuyerInformationFormActions.setBuyerInformationPrefillAndFormFields({
          prefillBuyerInformationForm,
          person,
          formFields: isReadOnlyBuyerFormFields
            ? disablePrefilledSavedBuyerFormFields({}, savedBuyerFormFields)
            : enableDisabledFormFields(savedBuyerFormFields)
        })
      )
    ),
  { functional: true }
);

export const setBuyerInformationPrefillOnAnonymousVoucherRelease$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(AnonymousVoucherActions.anonymousVoucherIsReleased),
      concatLatestFrom(() => store.select(selectSavedBuyer)),
      filter(([_, savedBuyerPerson]) => !isNil(savedBuyerPerson)),
      map(([_, { person, formFields }]) =>
        BuyerInformationFormActions.setBuyerInformationPrefillAndFormFields({
          prefillBuyerInformationForm: true,
          person,
          formFields: enableDisabledFormFields(formFields)
        })
      )
    ),
  { functional: true }
);

export const updateUnsavedBuyerToSavedBuyerOnSavedBuyerPersonUpdate$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), translateService = inject(TranslateService)) =>
    actions$.pipe(
      ofType(SavedTicketHolderActions.setUpdatedTicketHolder),
      filter(({ person, formFields }) => person.type === PersonType.Buyer && !isEmpty(formFields)),
      concatLatestFrom(() => [store.select(selectBuyerPerson), store.select(selectBuyerInformationFormFields)]),
      filter(([{ person, formFields }, buyerInformationPerson, buyerInformationFormFields]) => {
        const buyerInformationFormValues = mapValues(buyerInformationFormFields, 'value');
        const areEqualBuyerPersons = isEqual(person, buyerInformationPerson);
        const areEqualBuyerFormFields = areBuyerFormsFieldsEqual(
          formFields,
          buyerInformationFormValues,
          formFields,
          translateService
        );

        return !areEqualBuyerPersons && areEqualBuyerFormFields;
      }),
      map(([{ person, formFields }, buyerBillingAddressForm]) => ({
        savedPerson: person,
        currentPerson: buyerBillingAddressForm,
        formFields
      })),
      map((changeBuyerInformationPersonBackToSavedBuyerPerson) =>
        BuyerFormsSyncActions.changeBuyerInformationPersonBackToSavedBuyerPerson(
          changeBuyerInformationPersonBackToSavedBuyerPerson
        )
      )
    ),
  { functional: true }
);

export const setBuyerPersonBeforeSaveOrder$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SavedTicketHolderActions.setCreatedSavedTicketHolderPersonsBeforeSaveOrder),
      concatLatestFrom(() => store.select(selectBuyerPerson)),
      filter(([{ createdTicketHolders }, { id }]) => !isEmpty(createdTicketHolders) && isNull(id)),
      map(
        ([{ createdTicketHolders }]) =>
          find(createdTicketHolders, ({ person }) => person.type === PersonType.Buyer)?.person
      ),
      filter((person) => !isNil(person)),
      map((person) => BuyerInformationActions.setBuyerPerson(person))
    ),
  { functional: true }
);

export const getBuyerBuyerBillingAddressOnUserLogin$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType<SetProfile>(UserActionTypes.SET_PROFILE),
      filter(({ payload }) => !isNil(payload?.id)),
      map(({ payload }) => new GetProfileBillingAddress(payload.id))
    ),
  { functional: true }
);

export const setSelectedSavedBillingAddressIdAndFormFields$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(BuyerBillingAddressFormActions.setSelectedSavedBillingAddressId),
      concatLatestFrom(({ selectedSavedBillingAddressId }) =>
        store.pipe(select(selectProfileBillingAddressById(selectedSavedBillingAddressId)))
      ),
      map(([{ selectedSavedBillingAddressId }, savedBillingAddress]) => {
        const setSelectedSavedBillingAddress: SetSelectedSavedBillingAddress = {
          id: selectedSavedBillingAddressId,
          company: '',
          address: '',
          street: '',
          city: '',
          zipCode: '',
          country: ''
        };

        if (!isNil(savedBillingAddress)) {
          setSelectedSavedBillingAddress.company = savedBillingAddress.company;
          setSelectedSavedBillingAddress.address = savedBillingAddress.address;
          setSelectedSavedBillingAddress.street = savedBillingAddress.street;
          setSelectedSavedBillingAddress.city = savedBillingAddress.city;
          setSelectedSavedBillingAddress.zipCode = savedBillingAddress.zipCode;
          setSelectedSavedBillingAddress.country = !isEmpty(savedBillingAddress.country)
            ? combineTranslatedFormOptionKeyAndValue(FieldKey.Country, savedBillingAddress.country)
            : savedBillingAddress.country;
        }

        return BuyerBillingAddressFormActions.setSelectedSavedBillingAddressIdAndFormFields(
          setSelectedSavedBillingAddress
        );
      })
    ),
  { functional: true }
);
